// src/pages/rh/DetailEmploye.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Badge, Spinner, Alert,
    Modal, Table, Form, Tab, Nav, Image, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { COLORS } from "../../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   MODAL AJOUT DOCUMENT
   ============================================================ */
const AddDocumentModal = ({ show, onHide, onAdd, employeId }) => {
    const [document, setDocument] = useState({
        type_document: 'CNI',
        nom_document: '',
        date_expiration: '',
        fichier: null
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors({ fichier: "Le fichier ne doit pas dépasser 5 Mo" });
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setDocument(prev => ({ ...prev, fichier: reader.result }));
                setErrors({});
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!document.nom_document) newErrors.nom_document = "Le nom du document est requis";
        if (!document.fichier) newErrors.fichier = "Veuillez sélectionner un fichier";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        await onAdd({ ...document, employe_id: employeId });
        setLoading(false);
        setDocument({ type_document: 'CNI', nom_document: '', date_expiration: '', fichier: null });
        setErrors({});
        onHide();
    };

    return (
        <Modal show={show} onHide={onHide} centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #0d8ba6)` }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-file-add-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">Ajouter un document</Modal.Title>
                        <p className="text-white-50 mb-0 small">Renseignez les informations du document</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Type de document</Form.Label>
                        <Form.Select
                            value={document.type_document}
                            onChange={(e) => setDocument(prev => ({ ...prev, type_document: e.target.value }))}
                            className="input-select-modern"
                        >
                            <option value="CNI">CNI</option>
                            <option value="Passeport">Passeport</option>
                            <option value="Permis">Permis de conduire</option>
                            <option value="Diplome">Diplôme</option>
                            <option value="Attestation">Attestation</option>
                            <option value="Autre">Autre</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Nom du document</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ex: CNI 2024"
                            value={document.nom_document}
                            onChange={(e) => setDocument(prev => ({ ...prev, nom_document: e.target.value }))}
                            className="input-modern-field"
                            isInvalid={!!errors.nom_document}
                        />
                        <Form.Control.Feedback type="invalid">{errors.nom_document}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Date d'expiration</Form.Label>
                        <Form.Control
                            type="date"
                            value={document.date_expiration}
                            onChange={(e) => setDocument(prev => ({ ...prev, date_expiration: e.target.value }))}
                            className="input-modern-field"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="form-label-modern">Fichier</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".pdf,.jpg,.png,.doc,.docx"
                            onChange={handleFileChange}
                            className="input-modern-field"
                            isInvalid={!!errors.fichier}
                        />
                        <Form.Text className="text-muted small">PDF, JPG, PNG, DOC, DOCX (max 5 Mo)</Form.Text>
                        <Form.Control.Feedback type="invalid">{errors.fichier}</Form.Control.Feedback>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline">Annuler</Button>
                <Button onClick={handleSubmit} disabled={loading} className="btn-modern-secondary">
                    {loading ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : 'Ajouter'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   MODAL AJOUT CONGÉ
   ============================================================ */
const AddCongeModal = ({ show, onHide, onAdd, employeId }) => {
    const [conge, setConge] = useState({
        type_conge: 'annuel',
        date_debut: '',
        date_fin: '',
        motif: ''
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!conge.date_debut) newErrors.date_debut = "La date de début est requise";
        if (!conge.date_fin) newErrors.date_fin = "La date de fin est requise";
        if (conge.date_debut && conge.date_fin && new Date(conge.date_fin) < new Date(conge.date_debut)) {
            newErrors.date_fin = "La date de fin doit être postérieure à la date de début";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            await onAdd(conge);
            setConge({ type_conge: 'annuel', date_debut: '', date_fin: '', motif: '' });
            setErrors({});
        } catch (err) {
            console.error("Erreur lors de l'ajout:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-calendar-add-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">Ajouter un congé</Modal.Title>
                        <p className="text-white-50 mb-0 small">Saisissez la période du congé</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4">
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Type de congé</Form.Label>
                        <Form.Select
                            value={conge.type_conge}
                            onChange={(e) => setConge(prev => ({ ...prev, type_conge: e.target.value }))}
                            className="input-select-modern"
                        >
                            <option value="annuel">Annuel</option>
                            <option value="maladie">Maladie</option>
                            <option value="sans_solde">Sans solde</option>
                            <option value="maternite">Maternité</option>
                            <option value="formation">Formation</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Date de début</Form.Label>
                        <Form.Control
                            type="date"
                            value={conge.date_debut}
                            onChange={(e) => setConge(prev => ({ ...prev, date_debut: e.target.value }))}
                            className="input-modern-field"
                            isInvalid={!!errors.date_debut}
                        />
                        <Form.Control.Feedback type="invalid">{errors.date_debut}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Date de fin</Form.Label>
                        <Form.Control
                            type="date"
                            value={conge.date_fin}
                            onChange={(e) => setConge(prev => ({ ...prev, date_fin: e.target.value }))}
                            className="input-modern-field"
                            isInvalid={!!errors.date_fin}
                        />
                        <Form.Control.Feedback type="invalid">{errors.date_fin}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label className="form-label-modern">Motif (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={conge.motif}
                            onChange={(e) => setConge(prev => ({ ...prev, motif: e.target.value }))}
                            placeholder="Raison du congé..."
                            className="input-modern-field"
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline">Annuler</Button>
                <Button onClick={handleSubmit} disabled={loading} className="btn-modern-success">
                    {loading ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : 'Ajouter le congé'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   MODAL VISUALISATION DOCUMENT
   ============================================================ */
const DocumentViewerModal = ({ show, onHide, document }) => {
    const [isImage, setIsImage] = useState(false);
    const [isPdf, setIsPdf] = useState(false);

    useEffect(() => {
        if (document && document.fichier) {
            const fileType = document.fichier.split(';')[0];
            setIsImage(fileType.includes('image'));
            setIsPdf(fileType.includes('pdf'));
        }
    }, [document]);

    if (!document) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.info}, #0dcaf0)` }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-file-view-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">{document.nom_document}</Modal.Title>
                        <span className="badge-header-light mt-1 d-inline-block">{document.type_document}</span>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="p-3" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Row>
                        <Col md={4}>
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>Type de document</small>
                            <div className="mt-1"><span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.info}, #0dcaf0)` }}>{document.type_document}</span></div>
                        </Col>
                        <Col md={4}>
                            <small className="text-muted" style={{ fontSize: '0.7rem' }}>Date d'ajout</small>
                            <div className="fw-semibold mt-1" style={{ fontSize: '0.85rem' }}>{formatDate(document.created_at)}</div>
                        </Col>
                        {document.date_expiration && document.date_expiration !== "0000-00-00" && (
                            <Col md={4}>
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Date d'expiration</small>
                                <div className={`fw-semibold mt-1 ${new Date(document.date_expiration) < new Date() ? 'text-danger' : ''}`} style={{ fontSize: '0.85rem' }}>
                                    {formatDate(document.date_expiration)}
                                    {new Date(document.date_expiration) < new Date() && (
                                        <i className="ri-error-warning-line ms-1" title="Document expiré"></i>
                                    )}
                                </div>
                            </Col>
                        )}
                    </Row>
                </div>
                <div className="p-3 text-center" style={{ maxHeight: '60vh', overflow: 'auto', background: 'white' }}>
                    {isImage ? (
                        <img
                            src={document.fichier}
                            alt={document.nom_document}
                            className="img-fluid rounded-3"
                            style={{ maxHeight: '55vh', objectFit: 'contain' }}
                        />
                    ) : isPdf ? (
                        <iframe
                            src={document.fichier}
                            title={document.nom_document}
                            style={{ width: '100%', height: '55vh', border: 'none', borderRadius: '12px' }}
                        />
                    ) : (
                        <div className="text-center py-5">
                            <i className="ri-file-line text-muted mb-3 d-block" style={{ fontSize: '3rem' }}></i>
                            <p className="text-muted">Aperçu non disponible pour ce type de fichier</p>
                            <Button
                                onClick={() => window.open(document.fichier, '_blank')}
                                className="btn-modern-primary"
                            >
                                <i className="ri-download-line me-2"></i>Télécharger
                            </Button>
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline">Fermer</Button>
                <Button
                    onClick={() => window.open(document.fichier, '_blank')}
                    className="btn-modern-primary"
                >
                    <i className="ri-download-line me-2"></i>Télécharger
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   MODAL SUPPRESSION DOCUMENT
   ============================================================ */
const DeleteDocumentModal = ({ show, onHide, onConfirm, document }) => {
    const [deleting, setDeleting] = useState(false);

    const handleConfirm = async () => {
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };

    if (!document) return null;

    return (
        <Modal show={show} onHide={onHide} centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">Supprimer le document</Modal.Title>
                        <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-center text-muted mb-3">
                    Êtes-vous sûr de vouloir supprimer ce document ?
                </p>
                <div className="summary-card-charte mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-3" style={{ background: `${COLORS.info}15` }}>
                            <i className="ri-file-text-line" style={{ color: COLORS.info, fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                            <div className="fw-semibold">{document.nom_document}</div>
                            <small className="text-muted">{document.type_document}</small>
                            {document.date_expiration && document.date_expiration !== "0000-00-00" && (
                                <div className="small text-muted">
                                    Expire le {formatDate(document.date_expiration)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="alert-error-charte mb-0">
                    <div className="d-flex align-items-center">
                        <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                        <div className="ms-3">
                            <strong>Attention</strong>
                            <p className="mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline" disabled={deleting}>Annuler</Button>
                <Button onClick={handleConfirm} disabled={deleting} className="btn-modern-danger">
                    {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Supprimer</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   MODAL SUPPRESSION CONGÉ
   ============================================================ */
const DeleteCongeModal = ({ show, onHide, onConfirm, conge }) => {
    const [deleting, setDeleting] = useState(false);

    const handleConfirm = async () => {
        setDeleting(true);
        await onConfirm();
        setDeleting(false);
    };

    if (!conge) return null;

    return (
        <Modal show={show} onHide={onHide} centered className="modal-charte">
            <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                <div className="d-flex align-items-center gap-3">
                    <div className="modal-header-icon"><i className="ri-calendar-close-line"></i></div>
                    <div>
                        <Modal.Title className="fs-5 fw-bold text-white mb-0">Supprimer le congé</Modal.Title>
                        <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4">
                <p className="text-center text-muted mb-3">
                    Êtes-vous sûr de vouloir supprimer ce congé ?
                </p>
                <div className="summary-card-charte mb-3">
                    <div className="d-flex align-items-center gap-3">
                        <div className="p-2 rounded-3" style={{ background: `${COLORS.success}15` }}>
                            <i className="ri-calendar-line" style={{ color: COLORS.success, fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                            <div className="fw-semibold text-capitalize">{conge.type_conge}</div>
                            <small className="text-muted">
                                Du {formatDate(conge.date_debut)} au {formatDate(conge.date_fin)}
                            </small>
                        </div>
                    </div>
                </div>
                <div className="alert-error-charte mb-0">
                    <div className="d-flex align-items-center">
                        <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                        <div className="ms-3">
                            <strong>Attention</strong>
                            <p className="mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                <Button onClick={onHide} className="btn-modern-outline" disabled={deleting}>Annuler</Button>
                <Button onClick={handleConfirm} disabled={deleting} className="btn-modern-danger">
                    {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Supprimer</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailEmploye() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [employe, setEmploye] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showCongeModal, setShowCongeModal] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [showDeleteCongeModal, setShowDeleteCongeModal] = useState(false);
    const [congeToDelete, setCongeToDelete] = useState(null);
    const [showDocumentViewer, setShowDocumentViewer] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    useEffect(() => {
        if (id) fetchEmploye();
        else { setError("ID de l'employé non spécifié"); setLoading(false); }
    }, [id]);

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchEmploye = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            if (!token) { navigate("/pages/signin2"); return; }

            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 404) throw new Error("Employé non trouvé");
                if (response.status === 401) { navigate("/pages/signin2"); throw new Error("Session expirée"); }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const result = await response.json();
            if (result.success && result.data) {
                setEmploye(result.data);
                setEditForm(result.data);
            } else {
                throw new Error(result.message || "Employé non trouvé");
            }
        } catch (err) {
            console.error("Erreur fetchEmploye:", err);
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS
       ============================================================ */
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                showToast('success', 'Succès', "Employé supprimé avec succès");
                setTimeout(() => navigate('/rh/employes'), 1500);
            } else throw new Error(result.message || "Erreur lors de la suppression");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = async () => {
        setEditing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?id=${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(editForm)
            });
            const result = await response.json();
            if (result.success) {
                setShowEditModal(false);
                await fetchEmploye();
                showToast('success', 'Succès', "Employé modifié avec succès");
            } else throw new Error(result.message || "Erreur lors de la modification");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setEditing(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddDocument = async (document) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'document', ...document })
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Document ajouté avec succès");
                fetchEmploye();
                setShowDocumentModal(false);
            } else throw new Error(result.message || "Erreur lors de l'ajout");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    const handleViewDocument = (doc) => { setSelectedDocument(doc); setShowDocumentViewer(true); };
    const handleDeleteDocumentClick = (doc) => { setDocumentToDelete(doc); setShowDeleteDocumentModal(true); };

    const handleConfirmDeleteDocument = async () => {
        if (!documentToDelete) return;
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?document_id=${documentToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Document supprimé avec succès");
                fetchEmploye();
                setShowDeleteDocumentModal(false);
                setDocumentToDelete(null);
            } else throw new Error(result.message || "Erreur lors de la suppression");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    const handleAddConge = async (conge) => {
        try {
            const token = localStorage.getItem("token");
            const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
            const congeData = {
                action: 'conge',
                employe_id: employe.id,
                type_conge: conge.type_conge,
                date_debut: conge.date_debut,
                date_fin: conge.date_fin,
                motif: conge.motif || '',
                created_by: currentUser.id_utilisateur || currentUser.id || 1
            };
            if (!congeData.employe_id || !congeData.date_debut || !congeData.date_fin)
                throw new Error("Tous les champs obligatoires doivent être remplis");
            if (new Date(congeData.date_fin) < new Date(congeData.date_debut))
                throw new Error("La date de fin doit être postérieure à la date de début");

            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(congeData)
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Congé ajouté avec succès");
                fetchEmploye();
                setShowCongeModal(false);
            } else throw new Error(result.message || "Erreur lors de l'ajout");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    const handleDeleteCongeClick = (conge) => {
        if (!conge || !conge.id) { showToast('error', 'Erreur', "ID du congé manquant"); return; }
        setCongeToDelete(conge);
        setShowDeleteCongeModal(true);
    };

    const handleConfirmDeleteConge = async () => {
        if (!congeToDelete || !congeToDelete.id) {
            showToast('error', 'Erreur', "Aucun congé sélectionné");
            setShowDeleteCongeModal(false);
            return;
        }
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php?conge_id=${congeToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', "Congé supprimé avec succès");
                fetchEmploye();
                setShowDeleteCongeModal(false);
                setCongeToDelete(null);
            } else throw new Error(result.message || "Erreur lors de la suppression");
        } catch (err) {
            showToast('error', 'Erreur', err.message);
            setShowDeleteCongeModal(false);
        }
    };

    const handleUpdateCongeStatus = async (congeId, statut) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/employes.php`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ conge_id: congeId, statut })
            });
            const result = await response.json();
            if (result.success) {
                showToast('success', 'Succès', `Congé ${statut === 'approuve' ? 'approuvé' : 'refusé'}`);
                fetchEmploye();
            }
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const getStatutConfig = (statut) => {
        const configs = {
            actif:    { gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-checkbox-circle-line', label: 'Actif' },
            inactif:  { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-circle-line',    label: 'Inactif' },
            suspendu: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-pause-circle-line',    label: 'Suspendu' }
        };
        return configs[statut] || configs.actif;
    };

    const getStatutBadge = (statut) => {
        const config = getStatutConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const getCongeStatusConfig = (statut) => {
        const configs = {
            approuve:   { gradient: 'linear-gradient(135deg, #10b981, #059669)', icon: 'ri-check-line',        label: 'Approuvé' },
            refuse:     { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', icon: 'ri-close-line',        label: 'Refusé' },
            en_attente: { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', icon: 'ri-time-line',         label: 'En attente' }
        };
        return configs[statut] || configs.en_attente;
    };

    const getCongeStatusBadge = (statut) => {
        const config = getCongeStatusConfig(statut);
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const ActionButton = ({ icon, onClick, tooltip, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <button
                className="action-btn-charte"
                onClick={onClick}
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-user-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement des informations
                        </h5>
                        <p className="text-muted small">Récupération des données en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !employe) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Employé non trouvé"}</p>
                            </div>
                            <Button onClick={() => navigate('/rh/employes')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const statut = getStatutBadge(employe.statut);
    const age = employe.date_naissance ? new Date().getFullYear() - new Date(employe.date_naissance).getFullYear() : null;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">{employe.prenom} {employe.nom}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-fingerprint-line"></i>
                                        {employe.matricule}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-briefcase-line"></i>
                                        {employe.poste || 'Poste non défini'}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-building-line"></i>
                                        {employe.departement || 'Département non défini'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {statut}
                            <Button className="btn-modern-outline" onClick={() => navigate('/rh/employes')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-primary" onClick={() => setShowEditModal(true)}>
                                <i className="ri-edit-line me-1"></i> Modifier
                            </Button>
                            <Button className="btn-modern-danger" onClick={() => setShowDeleteModal(true)}>
                                <i className="ri-delete-bin-line me-1"></i> Supprimer
                            </Button>
                        </div>
                    </div>

                    {/* ===== CARTE PROFIL ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 profile-card-charte">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-4">
                                <Col md={2} className="text-center">
                                    <div className="profile-avatar-charte">
                                        {employe.photo ? (
                                            <Image src={employe.photo} roundedCircle className="profile-img-charte" />
                                        ) : (
                                            <i className="ri-user-line"></i>
                                        )}
                                    </div>
                                </Col>
                                <Col md={7}>
                                    <h4 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>
                                        {employe.prenom} {employe.nom}
                                    </h4>
                                    <div className="d-flex flex-wrap gap-3 mt-3">
                                        <span className="info-chip-charte">
                                            <i className="ri-mail-line"></i>
                                            {employe.email || 'Non renseigné'}
                                        </span>
                                        {employe.telephone && (
                                            <span className="info-chip-charte">
                                                <i className="ri-phone-line"></i>
                                                {employe.telephone}
                                            </span>
                                        )}
                                        <span className="info-chip-charte">
                                            <i className="ri-calendar-line"></i>
                                            Embauché le {formatDate(employe.date_embauche)}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={3} className="text-md-end">
                                    <div className="profile-stats-charte">
                                        <div className="profile-stat-item-charte">
                                            <span className="profile-stat-label">Matricule</span>
                                            <span className="profile-stat-value">{employe.matricule}</span>
                                        </div>
                                        <div className="profile-stat-item-charte">
                                            <span className="profile-stat-label">Contrat</span>
                                            <span className="profile-stat-value">{employe.type_contrat || '-'}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABS ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                            <Card.Header className="card-tabs-header-charte">
                                <Nav variant="tabs" className="tabs-charte">
                                    <Nav.Item>
                                        <Nav.Link eventKey="info">
                                            <span className="tab-title-charte">
                                                <i className="ri-information-line"></i>Informations
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="documents">
                                            <span className="tab-title-charte">
                                                <i className="ri-file-list-line"></i>Documents
                                                {employe.documents?.length > 0 && (
                                                    <span className="tab-badge-charte">{employe.documents.length}</span>
                                                )}
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="conges">
                                            <span className="tab-title-charte">
                                                <i className="ri-calendar-line"></i>Congés
                                                {employe.conges?.length > 0 && (
                                                    <span className="tab-badge-charte" style={{ background: COLORS.warning }}>{employe.conges.length}</span>
                                                )}
                                            </span>
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Tab.Content>

                                    {/* ===== INFORMATIONS ===== */}
                                    <Tab.Pane eventKey="info">
                                        <Row className="g-4">
                                            <Col md={6}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                        <i className="ri-user-line"></i>
                                                    </span>
                                                    <h6>Informations personnelles</h6>
                                                </div>
                                                <div className="info-card-charte">
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-user-line"></i>Nom complet</span>
                                                        <span className="info-value-charte">{employe.prenom} {employe.nom}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-calendar-line"></i>Date de naissance</span>
                                                        <span className="info-value-charte">{formatDate(employe.date_naissance)} {age && `(${age} ans)`}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-map-pin-line"></i>Lieu de naissance</span>
                                                        <span className="info-value-charte">{employe.lieu_naissance || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-earth-line"></i>Nationalité</span>
                                                        <span className="info-value-charte">{employe.nationalite || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-user-3-line"></i>Sexe</span>
                                                        <span className="info-value-charte">{employe.sexe === 'M' ? 'Masculin' : 'Féminin'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-heart-line"></i>Situation matrimoniale</span>
                                                        <span className="info-value-charte">{employe.situation_matrimoniale || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-parent-line"></i>Nombre d'enfants</span>
                                                        <span className="info-value-charte">{employe.nombre_enfants || 0}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-map-pin-2-line"></i>Adresse</span>
                                                        <span className="info-value-charte">{employe.adresse || '—'}</span>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                        <i className="ri-briefcase-line"></i>
                                                    </span>
                                                    <h6>Informations professionnelles</h6>
                                                </div>
                                                <div className="info-card-charte">
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-fingerprint-line"></i>Matricule</span>
                                                        <span className="info-value-charte fw-semibold">{employe.matricule}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-briefcase-4-line"></i>Poste</span>
                                                        <span className="info-value-charte">{employe.poste || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-building-line"></i>Département</span>
                                                        <span className="info-value-charte">{employe.departement || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-calendar-check-line"></i>Date d'embauche</span>
                                                        <span className="info-value-charte">{formatDate(employe.date_embauche)}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-file-text-line"></i>Type de contrat</span>
                                                        <span className="info-value-charte">{employe.type_contrat || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-money-euro-circle-line"></i>Salaire de base</span>
                                                        <span className="info-value-charte fw-bold" style={{ color: COLORS.primary }}>
                                                            {new Intl.NumberFormat('fr-FR').format(employe.salaire_base || 0)} FCFA
                                                        </span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-mail-line"></i>Email pro.</span>
                                                        <span className="info-value-charte">{employe.email || '—'}</span>
                                                    </div>
                                                    <div className="info-row-charte">
                                                        <span className="info-label-charte"><i className="ri-phone-line"></i>Téléphone</span>
                                                        <span className="info-value-charte">{employe.telephone || '—'}</span>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Tab.Pane>

                                    {/* ===== DOCUMENTS ===== */}
                                    <Tab.Pane eventKey="documents">
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                            <div className="section-title-charte mb-0">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                    <i className="ri-file-list-line"></i>
                                                </span>
                                                <h6 className="mb-0">Documents ({employe.documents?.length || 0})</h6>
                                            </div>
                                            <Button className="btn-modern-secondary" onClick={() => setShowDocumentModal(true)}>
                                                <i className="ri-file-add-line me-1"></i>Ajouter un document
                                            </Button>
                                        </div>

                                        {employe.documents && employe.documents.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table hover className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th className="ps-4">Type</th>
                                                            <th>Nom du document</th>
                                                            <th>Expiration</th>
                                                            <th>Date ajout</th>
                                                            <th className="text-center pe-4">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {employe.documents.map(doc => (
                                                            <tr key={doc.id}>
                                                                <td className="ps-4">
                                                                    <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.info}, #0dcaf0)` }}>
                                                                        {doc.type_document}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <strong style={{ fontSize: '0.85rem', color: '#1e293b' }}>{doc.nom_document}</strong>
                                                                    {doc.fichier && (
                                                                        <div className="mt-1">
                                                                            <button className="btn-link-modern" onClick={() => handleViewDocument(doc)}>
                                                                                <i className="ri-eye-line me-1"></i>Voir le document
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {doc.date_expiration && doc.date_expiration !== "0000-00-00" ? (
                                                                        <span className={new Date(doc.date_expiration) < new Date() ? 'text-danger fw-semibold' : ''} style={{ fontSize: '0.85rem' }}>
                                                                            {formatDate(doc.date_expiration)}
                                                                            {new Date(doc.date_expiration) < new Date() && (
                                                                                <i className="ri-error-warning-line ms-1 text-danger" title="Document expiré"></i>
                                                                            )}
                                                                        </span>
                                                                    ) : '—'}
                                                                </td>
                                                                <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDate(doc.created_at)}</td>
                                                                <td className="text-center pe-4">
                                                                    <div className="d-flex gap-2 justify-content-center">
                                                                        <ActionButton
                                                                            icon="ri-eye-line"
                                                                            onClick={() => handleViewDocument(doc)}
                                                                            tooltip="Voir le document"
                                                                        />
                                                                        <ActionButton
                                                                            icon="ri-delete-bin-line"
                                                                            onClick={() => handleDeleteDocumentClick(doc)}
                                                                            tooltip="Supprimer"
                                                                            color={COLORS.danger}
                                                                        />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.primary}05)`, color: COLORS.primary }}>
                                                    <i className="ri-file-list-line"></i>
                                                </div>
                                                <h6 className="fw-semibold mb-1">Aucun document</h6>
                                                <p className="text-muted small mb-3">Aucun document enregistré pour cet employé</p>
                                                <Button className="btn-modern-primary" onClick={() => setShowDocumentModal(true)}>
                                                    <i className="ri-file-add-line me-1"></i>Ajouter un document
                                                </Button>
                                            </div>
                                        )}
                                    </Tab.Pane>

                                    {/* ===== CONGÉS ===== */}
                                    <Tab.Pane eventKey="conges">
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                            <div className="section-title-charte mb-0">
                                                <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                    <i className="ri-calendar-line"></i>
                                                </span>
                                                <h6 className="mb-0">Congés ({employe.conges?.length || 0})</h6>
                                            </div>
                                            <Button className="btn-modern-success" onClick={() => setShowCongeModal(true)}>
                                                <i className="ri-calendar-add-line me-1"></i>Ajouter un congé
                                            </Button>
                                        </div>

                                        {employe.conges && employe.conges.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table hover className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th className="ps-4">Type</th>
                                                            <th>Période</th>
                                                            <th>Motif</th>
                                                            <th className="text-center">Statut</th>
                                                            <th>Date demande</th>
                                                            <th className="text-center pe-4">Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {employe.conges.map(conge => {
                                                            const dateDebut = new Date(conge.date_debut);
                                                            const dateFin = new Date(conge.date_fin);
                                                            const duree = Math.ceil((dateFin - dateDebut) / (1000 * 60 * 60 * 24)) + 1;
                                                            return (
                                                                <tr key={conge.id}>
                                                                    <td className="ps-4">
                                                                        <span className="status-badge-charte" style={{ background: `linear-gradient(135deg, ${COLORS.secondary}, #0d8ba6)` }}>
                                                                            {conge.type_conge === 'annuel' && <i className="ri-sun-line"></i>}
                                                                            {conge.type_conge === 'maladie' && <i className="ri-hospital-line"></i>}
                                                                            {conge.type_conge === 'sans_solde' && <i className="ri-money-euro-circle-line"></i>}
                                                                            {conge.type_conge === 'maternite' && <i className="ri-baby-line"></i>}
                                                                            {conge.type_conge === 'formation' && <i className="ri-book-open-line"></i>}
                                                                            {conge.type_conge}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ minWidth: '220px' }}>
                                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem', color: '#1e293b' }}>
                                                                            <i className="ri-calendar-line me-1 text-muted"></i>
                                                                            {formatDate(conge.date_debut)} → {formatDate(conge.date_fin)}
                                                                        </div>
                                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                            <i className="ri-time-line me-1"></i>Durée: {duree} jour(s)
                                                                        </small>
                                                                    </td>
                                                                    <td style={{ minWidth: '200px' }}>
                                                                        {conge.motif ? (
                                                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '250px', fontSize: '0.85rem', color: '#475569' }} title={conge.motif}>
                                                                                {conge.motif}
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td className="text-center">{getCongeStatusBadge(conge.statut)}</td>
                                                                    <td>
                                                                        <div className="small" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                                                            <i className="ri-calendar-todo-line me-1"></i>
                                                                            {formatDate(conge.created_at)}
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-center pe-4">
                                                                        <div className="d-flex gap-2 justify-content-center">
                                                                            {conge.statut === 'en_attente' && (
                                                                                <>
                                                                                    <ActionButton
                                                                                        icon="ri-check-line"
                                                                                        onClick={() => handleUpdateCongeStatus(conge.id, 'approuve')}
                                                                                        tooltip="Approuver"
                                                                                        color={COLORS.success}
                                                                                    />
                                                                                    <ActionButton
                                                                                        icon="ri-close-line"
                                                                                        onClick={() => handleUpdateCongeStatus(conge.id, 'refuse')}
                                                                                        tooltip="Refuser"
                                                                                        color={COLORS.danger}
                                                                                    />
                                                                                </>
                                                                            )}
                                                                            <ActionButton
                                                                                icon="ri-delete-bin-line"
                                                                                onClick={() => handleDeleteCongeClick(conge)}
                                                                                tooltip="Supprimer"
                                                                                color={COLORS.danger}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}15, ${COLORS.success}05)`, color: COLORS.success }}>
                                                    <i className="ri-calendar-line"></i>
                                                </div>
                                                <h6 className="fw-semibold mb-1">Aucun congé</h6>
                                                <p className="text-muted small mb-3">Aucun congé enregistré pour cet employé</p>
                                                <Button className="btn-modern-success" onClick={() => setShowCongeModal(true)}>
                                                    <i className="ri-calendar-add-line me-1"></i>Ajouter un congé
                                                </Button>
                                            </div>
                                        )}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Card.Body>
                        </Tab.Container>
                    </Card>

                    <div className="text-center text-muted small mt-4" style={{ fontSize: '0.7rem' }}>
                        <i className="ri-information-line me-1"></i>
                        Créé le {formatDate(employe.created_at)}
                        {employe.updated_at && employe.updated_at !== employe.created_at && ` • Modifié le ${formatDate(employe.updated_at)}`}
                    </div>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL SUPPRESSION EMPLOYÉ ===== */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-delete-bin-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la suppression</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-center text-muted mb-3">
                        Êtes-vous sûr de vouloir supprimer cet employé ?
                    </p>
                    <div className="summary-card-charte mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="employe-avatar-charte">
                                {employe.prenom?.charAt(0)}{employe.nom?.charAt(0)}
                            </div>
                            <div>
                                <div className="fw-bold" style={{ color: COLORS.primaryDark }}>{employe.prenom} {employe.nom}</div>
                                <small className="text-muted">Matricule: {employe.matricule}</small>
                                <div className="small text-muted">{employe.poste} — {employe.departement}</div>
                            </div>
                        </div>
                    </div>
                    <div className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Toutes les données associées seront définitivement supprimées</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowDeleteModal(false)} className="btn-modern-outline" disabled={deleting}>Annuler</Button>
                    <Button onClick={handleDelete} disabled={deleting} className="btn-modern-danger">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : <><i className="ri-delete-bin-line me-2"></i>Confirmer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL MODIFICATION EMPLOYÉ ===== */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-edit-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Modifier l'employé</Modal.Title>
                            <p className="text-white-50 mb-0 small">Mettez à jour les informations</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Form>
                        <Row className="g-3">
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Nom</Form.Label><Form.Control type="text" name="nom" value={editForm.nom || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Prénom</Form.Label><Form.Control type="text" name="prenom" value={editForm.prenom || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Email</Form.Label><Form.Control type="email" name="email" value={editForm.email || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Téléphone</Form.Label><Form.Control type="tel" name="telephone" value={editForm.telephone || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={12}><Form.Group><Form.Label className="form-label-modern">Adresse</Form.Label><Form.Control as="textarea" rows={2} name="adresse" value={editForm.adresse || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Poste</Form.Label><Form.Control type="text" name="poste" value={editForm.poste || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Département</Form.Label><Form.Control type="text" name="departement" value={editForm.departement || ''} onChange={handleEditChange} className="input-modern-field" /></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Type de contrat</Form.Label><Form.Select name="type_contrat" value={editForm.type_contrat || 'CDI'} onChange={handleEditChange} className="input-select-modern"><option value="CDI">CDI</option><option value="CDD">CDD</option><option value="Stage">Stage</option><option value="Alternance">Alternance</option></Form.Select></Form.Group></Col>
                            <Col md={6}><Form.Group><Form.Label className="form-label-modern">Salaire de base (FCFA)</Form.Label><Form.Control type="number" name="salaire_base" value={editForm.salaire_base || ''} onChange={handleEditChange} min="0" step="1000" className="input-modern-field" /></Form.Group></Col>
                            <Col md={12}><Form.Group><Form.Label className="form-label-modern">Statut</Form.Label><Form.Select name="statut" value={editForm.statut || 'actif'} onChange={handleEditChange} className="input-select-modern"><option value="actif">Actif</option><option value="inactif">Inactif</option><option value="suspendu">Suspendu</option></Form.Select></Form.Group></Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowEditModal(false)} className="btn-modern-outline">Annuler</Button>
                    <Button onClick={handleEdit} disabled={editing} className="btn-modern-primary">
                        {editing ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-save-line me-2"></i>Enregistrer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODALS AJOUT ===== */}
            <AddDocumentModal show={showDocumentModal} onHide={() => setShowDocumentModal(false)} onAdd={handleAddDocument} employeId={employe.id} />
            <AddCongeModal show={showCongeModal} onHide={() => setShowCongeModal(false)} onAdd={handleAddConge} employeId={employe.id} />

            {/* ===== MODALS SUPPRESSION ===== */}
            <DeleteDocumentModal show={showDeleteDocumentModal} onHide={() => { setShowDeleteDocumentModal(false); setDocumentToDelete(null); }} onConfirm={handleConfirmDeleteDocument} document={documentToDelete} />
            <DeleteCongeModal show={showDeleteCongeModal} onHide={() => { setShowDeleteCongeModal(false); setCongeToDelete(null); }} onConfirm={handleConfirmDeleteConge} conge={congeToDelete} />

            {/* ===== MODAL VISUALISATION DOCUMENT ===== */}
            <DocumentViewerModal show={showDocumentViewer} onHide={() => { setShowDocumentViewer(false); setSelectedDocument(null); }} document={selectedDocument} />

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.1rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.4rem;
                }

                /* ===== PROFIL CARD ===== */
                .profile-card-charte { background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); }
                .profile-avatar-charte {
                    width: 100px; height: 100px; border-radius: 20px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary); font-size: 2.5rem;
                    border: 2px solid ${COLORS.primary}25;
                    margin: 0 auto;
                    overflow: hidden;
                }
                .profile-img-charte {
                    width: 100%; height: 100%; object-fit: cover;
                }
                .info-chip-charte {
                    display: inline-flex; align-items: center; gap: 6px;
                    background: #f1f5f9; color: #475569;
                    padding: 0.35rem 0.9rem; border-radius: 50px;
                    font-size: 0.8rem; font-weight: 500;
                }
                .info-chip-charte i { color: var(--secondary); }
                .profile-stats-charte {
                    background: white; padding: 1rem 1.25rem;
                    border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .profile-stat-item-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.5rem 0; border-bottom: 1px dashed #e2e8f0;
                }
                .profile-stat-item-charte:last-child { border-bottom: none; }
                .profile-stat-label { font-size: 0.8rem; color: #94a3b8; font-weight: 500; }
                .profile-stat-value { font-size: 0.95rem; font-weight: 700; color: #1e293b; }

                /* ===== TABS ===== */
                .card-tabs-header-charte {
                    background: white; border: none;
                    padding: 0;
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                }
                .tabs-charte { border-bottom: 1px solid #e2e8f0; padding: 0 1rem; gap: 0.25rem; }
                .tabs-charte .nav-link {
                    border: none; color: #64748b;
                    padding: 1rem 1.25rem;
                    font-weight: 500; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    border-bottom: 3px solid transparent;
                    background: transparent;
                }
                .tabs-charte .nav-link:hover {
                    color: var(--primary); background: #f8fafc;
                    border-bottom-color: #e2e8f0;
                }
                .tabs-charte .nav-link.active {
                    color: var(--primary);
                    border-bottom-color: var(--primary);
                    background: transparent;
                }
                .tab-title-charte { display: inline-flex; align-items: center; gap: 8px; }
                .tab-badge-charte {
                    background: var(--primary); color: white;
                    border-radius: 50px; padding: 0.1rem 0.5rem;
                    font-size: 0.65rem; font-weight: 700;
                    min-width: 20px; text-align: center;
                }

                /* ===== SECTIONS ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== INFO CARD ===== */
                .info-card-charte {
                    background: #f8fafc; border-radius: 12px;
                    border: 1px solid #e2e8f0; padding: 0.5rem 1rem;
                }
                .info-row-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                .info-row-charte:last-child { border-bottom: none; }
                .info-label-charte {
                    display: inline-flex; align-items: center; gap: 8px;
                    color: #64748b; font-size: 0.8rem; font-weight: 500;
                }
                .info-label-charte i { color: var(--secondary); font-size: 0.9rem; }
                .info-value-charte {
                    font-weight: 600; color: #1e293b;
                    font-size: 0.85rem; text-align: right;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .btn-link-modern {
                    background: transparent; border: none;
                    padding: 0; color: var(--primary);
                    font-size: 0.75rem; font-weight: 500;
                    text-decoration: none;
                    transition: color 0.2s;
                    cursor: pointer;
                }
                .btn-link-modern:hover { color: var(--primary-dark); text-decoration: underline; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.7rem;
                    font-weight: 600;
                }
                .summary-card-charte {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .employe-avatar-charte {
                    width: 52px; height: 52px; border-radius: 12px;
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 1.15rem;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.25);
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern-field {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern-field:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-select-modern {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-select-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-secondary,
                .btn-modern-success, .btn-modern-danger {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-secondary {
                    background: linear-gradient(135deg, var(--secondary), #0d8ba6);
                    box-shadow: 0 4px 12px rgba(33,172,205,0.3);
                }
                .btn-modern-secondary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(33,172,205,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled,
                .btn-modern-secondary:disabled,
                .btn-modern-success:disabled,
                .btn-modern-danger:disabled { opacity: 0.6; transform: none; }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .profile-avatar-charte { width: 80px; height: 80px; font-size: 2rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}